import { Loader2, Zap } from 'lucide-react'
import { DatePicker } from '@/components/ui/DatePicker'
import type { ChargebackDecisionTabProps } from '../../types/components'
import {
  CHARGEBACK_DETAIL_CARD_CLASS,
  CHARGEBACK_DETAIL_CHECKBOX_CARD_CLASS,
  CHARGEBACK_DETAIL_INPUT_CLASS,
  CHARGEBACK_DETAIL_PRIMARY_BUTTON_CLASS,
  CHARGEBACK_RESULT_OPTIONS,
  DECISION_FILE_TYPES,
  LOST_REASON_OPTIONS,
  YES_NO_OPTIONS,
} from '../../constants/chargebackDetailConstants'
import { isDecisionAllowed } from '../../utils/chargebackDetailFormatters'
import { ChargebackAddNotes } from './ChargebackAddNotes'
import { ChargebackDetailField } from './ChargebackDetailField'
import { chargebackDetailSelectOptions } from './chargebackDetailSelectOptions'
import { ChargebackFileUploadSection } from './ChargebackFileUploadSection'

export function ChargebackDecisionTab({ applicantId, page }: ChargebackDecisionTabProps) {
  const {
    detail,
    decision,
    patchDecision,
    saving,
    fileBusy,
    noteDraft,
    setNoteDraft,
    saveActiveTab,
    addNote,
    handleUpload,
    setDeleteFileId,
  } = page

  return (
    <section className={`${CHARGEBACK_DETAIL_CARD_CLASS} p-6`}>
      {!isDecisionAllowed(detail) ? (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Decision is available after the chargeback status is Challenged or Chargeback Complete.
        </div>
      ) : null}
      <div
        className={
          !isDecisionAllowed(detail) ? 'pointer-events-none opacity-60' : undefined
        }
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:divide-x lg:divide-slate-100">
          {/* Left Column: Decision Form & Notes */}
          <div className="space-y-5 lg:pr-6">
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
              <ChargebackDetailField label="Result">
                <select
                  className={CHARGEBACK_DETAIL_INPUT_CLASS}
                  value={decision.chargeback_type}
                  onChange={(e) =>
                    patchDecision({
                      chargeback_type: e.target.value,
                      ...(e.target.value === 'WON_CREDIT'
                        ? { lost_reason: '' }
                        : { deposited_liberty: '' }),
                    })
                  }
                >
                  {chargebackDetailSelectOptions(CHARGEBACK_RESULT_OPTIONS, 'Select Result')}
                </select>
              </ChargebackDetailField>

              {decision.chargeback_type === 'WON_CREDIT' ? (
                <ChargebackDetailField label="Deposited To Liberty Rent">
                  <select
                    className={CHARGEBACK_DETAIL_INPUT_CLASS}
                    value={decision.deposited_liberty}
                    onChange={(e) => patchDecision({ deposited_liberty: e.target.value })}
                  >
                    {chargebackDetailSelectOptions(YES_NO_OPTIONS, 'Select Status')}
                  </select>
                </ChargebackDetailField>
              ) : (
                <ChargebackDetailField label="Lost Reason">
                  <select
                    className={CHARGEBACK_DETAIL_INPUT_CLASS}
                    value={decision.lost_reason}
                    onChange={(e) => patchDecision({ lost_reason: e.target.value })}
                  >
                    {chargebackDetailSelectOptions(LOST_REASON_OPTIONS, 'Select Reason')}
                  </select>
                </ChargebackDetailField>
              )}

              <ChargebackDetailField label="Merchant Decision Date">
                <DatePicker
                  value={decision.merchant_decision_date}
                  onChange={(value) => patchDecision({ merchant_decision_date: value })}
                  className={CHARGEBACK_DETAIL_INPUT_CLASS}
                  showHelperText={false}
                  allowFutureDates
                />
              </ChargebackDetailField>

              <ChargebackDetailField
                label={
                  decision.chargeback_type === 'WON_CREDIT'
                    ? 'LR Deposit Date'
                    : 'Funds Withdrawn Date'
                }
              >
                <DatePicker
                  value={decision.funds_date}
                  onChange={(value) => patchDecision({ funds_date: value })}
                  className={CHARGEBACK_DETAIL_INPUT_CLASS}
                  showHelperText={false}
                  allowFutureDates
                />
              </ChargebackDetailField>

              <div className="md:col-span-2">
                <ChargebackFileUploadSection
                  title="File upload"
                  fieldName="decision_file"
                  fileTypes={DECISION_FILE_TYPES}
                  uploadedType="DECISION_FILE"
                  detail={detail}
                  applicantId={applicantId}
                  busy={fileBusy}
                  onUpload={handleUpload}
                  onDelete={setDeleteFileId}
                />
              </div>

              <div className="md:col-span-2">
                <ChargebackAddNotes
                  noteDraft={noteDraft}
                  setNoteDraft={setNoteDraft}
                  saving={saving}
                  addNote={addNote}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                disabled={saving}
                onClick={() => void saveActiveTab()}
                className={CHARGEBACK_DETAIL_PRIMARY_BUTTON_CLASS}
              >
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save
              </button>
            </div>
          </div>

          {/* Right Column: Follow-Up Actions */}
          <div className="space-y-4 lg:pl-6">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
                <Zap className="h-4 w-4" aria-hidden />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                Follow-Up Actions
              </h3>
            </div>

            <div className="space-y-2.5">
              {(
                [
                  ['expire_application', 'Expire Application'],
                  ['cancelled_application', 'Cancel Certificate'],
                  ['re_invoice', 'Re-invoice'],
                  ['file_fraud', 'File Fraud'],
                  ['refunded', 'Refunded'],
                ] as const
              ).map(([key, label]) => (
                <label
                  key={key}
                  className={CHARGEBACK_DETAIL_CHECKBOX_CARD_CLASS}
                >
                  <input
                    type="checkbox"
                    checked={decision[key]}
                    onChange={(event) => patchDecision({ [key]: event.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-[#1597ff] focus:ring-[#1597ff]"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
